<template>
  <div class="audio-player" :class="{ expanded: isExpanded, playing: isPlaying }" @mouseenter="isExpanded = true" @mouseleave="isExpanded = false">
    <div class="cover" :style="{ backgroundImage: `url(${coverSrc})` }"></div>
    <div class="meta">
      <div class="title" :title="title">{{ title }}</div>
      <div class="controls">
        <button class="btn" @click="togglePlay" :aria-label="isPlaying ? '暂停' : '播放'">
          <i :class="isPlaying ? 'fas fa-pause' : 'fas fa-play'" />
        </button>
      </div>
    </div>
    <audio
      ref="audioRef"
      :src="audioSrc"
      preload="auto"
      autoplay
      muted
      playsinline
      @canplay="onCanPlay"
      @ended="onEnded"
    ></audio>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import jsmediatags from 'jsmediatags/dist/jsmediatags.min.js'

export default {
  name: 'AudioPlayer',
  setup() {
    // 源与状态
  const audioSrc = '/A Life Sent On.mp3'
    const audioRef = ref(null)
    const isPlaying = ref(false)
  const isExpanded = ref(false)

    const title = ref('A Life Sent On')
  const coverSrc = ref('/icon.png')

    const togglePlay = async () => {
      const el = audioRef.value
      if (!el) return
      if (isPlaying.value) {
        el.pause()
        isPlaying.value = false
      } else {
        try {
          await el.play()
          isPlaying.value = true
          el.muted = false
        } catch (e) {
          // 如果被拦截，尝试以静音播放作为回退
          try {
            el.muted = true
            await el.play()
            isPlaying.value = true
          } catch (_) {}
        }
      }
    }

    const onCanPlay = async () => {
      // 首次可播放时尝试自动播放
      const el = audioRef.value
      if (!el) return
      el.volume = 0.7
      try {
        await el.play()
        isPlaying.value = true
        el.muted = false
      } catch (e) {
        // 回退为静音自动播放
        try {
          el.muted = true
          await el.play()
          isPlaying.value = true
        } catch (_) {}
      }
    }

    const onEnded = () => {
      isPlaying.value = false
    }

    const loadTags = async () => {
      const handleSuccess = ({ tags }) => {
        if (tags?.title) title.value = tags.title
        const pic = tags?.picture
        if (pic && pic.data && pic.format) {
          let base64 = ''
          const bytes = new Uint8Array(pic.data)
          const len = bytes.byteLength
          for (let i = 0; i < len; i++) base64 += String.fromCharCode(bytes[i])
          coverSrc.value = `data:${pic.format};base64,${btoa(base64)}`
        }
      }

      // 1) 首选使用 Reader 直接读取 URL（同源）
      try {
        new jsmediatags.Reader(audioSrc)
          .setTagsToRead(["title", "picture"]) 
          .read({ onSuccess: handleSuccess, onError: () => {} })
        return
      } catch (_) {
        // 忽略，进入回退方案
      }

      // 2) 回退：fetch -> Blob -> jsmediatags.read
      try {
        const resp = await fetch(audioSrc)
        const buf = await resp.arrayBuffer()
        const blob = new Blob([buf])
        jsmediatags.read(blob, { onSuccess: handleSuccess, onError: () => {} })
      } catch (_) {
        // 仍失败则保持默认封面
      }
    }

    onMounted(() => {
      loadTags()
      // 开页即尝试自动播放；若被策略阻止，则静音回退，并在首次交互时取消静音
      const tryAutoplay = async () => {
        const el = audioRef.value
        if (!el) return
        el.volume = 0.7
        try {
          el.muted = false
          await el.play()
          isPlaying.value = true
          return
        } catch (_) {}
        try {
          el.muted = true
          await el.play()
          isPlaying.value = true
        } catch (_) {}
      }
      tryAutoplay()

      // 如果静音在播放，首次交互时取消静音
      const resume = async () => {
        const el = audioRef.value
        if (!el) return
        try {
          if (el.muted) {
            el.muted = false
            el.volume = 0.7
          }
          await el.play()
          isPlaying.value = true
          window.removeEventListener('pointerdown', resume)
          window.removeEventListener('keydown', resume)
        } catch (_) {}
      }
      window.addEventListener('pointerdown', resume, { once: false })
      window.addEventListener('keydown', resume, { once: false })
    })

    return {
      audioSrc,
      audioRef,
      isPlaying,
      isExpanded,
      title,
      coverSrc,
      togglePlay,
      onCanPlay,
      onEnded
    }
  }
}
</script>

<style scoped lang="scss">
.audio-player {
  position: fixed;
  left: 16px;
  bottom: 16px;
  height: 56px;
  width: 56px; // 折叠宽度
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid #e5e7eb;
  border-radius: 50%; // 默认圆形
  box-shadow: 0 8px 24px rgba(0,0,0,0.12);
  display: flex;
  align-items: center;
  overflow: hidden;
  transition: width .25s ease, box-shadow .25s ease, border-radius .25s ease;
  z-index: 1200;

  &.expanded {
    width: 320px; // 展开宽度
    border-radius: 14px; // 展开时变为圆角矩形
  }

  &.playing:not(.expanded) {
    animation: spin 3s linear infinite;
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.cover {
  flex: 0 0 56px;
  height: 56px;
  background-size: cover;
  background-position: center;
  border-radius: inherit; // 继承父元素的圆角
}

.meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: .5rem;
  padding: 0 .75rem;
  width: 100%;
}

.title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.controls {
  display: flex;
  align-items: center;
  gap: .5rem;
}

.btn {
  width: 36px;
  height: 36px;
  border-radius: 9999px; // 强制正圆
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #111827;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex: 0 0 36px; // 不允许被压缩导致半圆
}

// 移除提示文字样式

@media (max-width: 768px) {
  .audio-player {
    bottom: 12px;
    left: 12px;
    height: 52px;
    width: 52px;
  }
  .audio-player.expanded { width: 280px; }
  .cover { flex-basis: 52px; height: 52px; }
}
</style>
